'use server';

/**
 * Stale artifacts of a story (edits after ready): the workspace shows
 * «Устарело» + «Обновить» next to each, and the exports that wait for it.
 * «Обновить» is an add-on order with its own estimate.
 */
import { SessionService } from '@/contexts/authentication';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import type { ActionResult } from '@/shared/types/actions';
import type { StaleView } from '../edits/editContext.server';
import type { ArtifactRef, EditEstimate } from '../edits/types';

export async function getStaleView(storyId: string): Promise<StaleView | null> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId || !storyId) return null;
  const { loadStaleView } = await import('../edits/editContext.server');
  return loadStaleView(storyId, familyId).catch(() => null);
}

/**
 * «Обновить» for these stale keys (all when empty): checked against the
 * guards, estimated, and handed to the executor's update order. Besides the
 * stale view's keys: `export:<layerId>` rebuilds an export once its inputs are
 * fresh, `transl:<storyId>:<blockId>` translates a source block again
 * (`confirmOverwrite`: translation blocks the parent edited that may be
 * overwritten — the others wait for the diff dialog).
 */
export async function requestStaleUpdate(
  storyId: string, keys: string[] = [], opts: { confirmOverwrite?: string[] } = {},
): Promise<ActionResult<{ estimate: EditEstimate; keys: string[] }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };
    const { guardEdit } = await import('../edits/guardAction.server');
    const blocked = await guardEdit({ storyId, kind: 'redraw_page' });
    if (blocked) return { success: false, error: blocked.message, code: 'EDIT_FORBIDDEN' };
    const { loadStaleView } = await import('../edits/editContext.server');
    const view = await loadStaleView(storyId, familyId);
    if (!view) return { success: false, error: 'Not found' };
    const want = new Set(keys);
    const items: ArtifactRef[] = keys.length ? view.items.filter((i) => want.has(i.key)) : [...view.items];
    for (const k of keys) {
      const [kind, a, b] = k.split(':');
      if (kind === 'export' && a) items.push({ artifact: 'export', key: k, exportId: a });
      if (kind === 'transl' && a && b) items.push({ artifact: 'transl', key: k, storyId: a, blockId: b });
    }
    if (!items.length) return { success: false, error: 'Not found', code: 'NOTHING_STALE' };
    const { runStaleUpdate } = await import('../edits/updateHook.server');
    await runStaleUpdate({ storyId, familyId, refs: items, ...(opts.confirmOverwrite?.length ? { confirmOverwrite: opts.confirmOverwrite } : {}) });
    return { success: true, data: { estimate: view.estimate ?? { credits: 0, pages: 0, covers: 0, sheets: 0, audioBlocks: 0, assemblies: 0 }, keys: items.map((i) => i.key) } };
  } catch (e: unknown) {
    const code = e && typeof e === 'object' && (e as { name?: string }).name === 'StaleUpdateRefused' ? (e as { code?: string }).code : undefined;
    if (code) return { success: false, error: code === 'INSUFFICIENT_ALLOWANCE' ? 'insufficient_allowance' : (e as Error).message, code };
    return { success: false, error: actionErrorMessage('requestStaleUpdate', e) };
  }
}

/**
 * The plan of an edit before it is applied (planEdit over the story's edit
 * context): what goes stale, the estimate of «Обновить», whether it asks
 * first, or why it is refused. Null: a v1 story (no v2 data) — apply as before.
 */
export async function planStoryEdit(storyId: string, kind: import('../edits/types').EditKind, target: import('../edits/types').EditTarget): Promise<import('../edits/types').EditPlan | null> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId || !storyId) return null;
  const { loadEditContext } = await import('../edits/editContext.server');
  const ctx = await loadEditContext(storyId, familyId).catch(() => null);
  if (!ctx) return null;
  const { planEdit } = await import('../edits/planEdit');
  const { defaultOrderRates } = await import('../order/server');
  const r = await defaultOrderRates();
  return planEdit(kind, target, ctx.state, { rates: { page: r.page, cover: r.cover, reference: r.reference, blockAudio: r.blockAudio, assembly: r.assembly } });
}
